import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { ComponentProps, ReactNode } from "react";
import { Input } from "@/components/ui/input/input";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";

type TextFieldBaseProps = Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange" | "onBlur" | "onClear" | "className"
> & {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner Input. */
  inputClassName?: string;
  /** Called after the field's own handleBlur. */
  onBlur?: () => void;
  /** Fired AFTER the value is committed — for side effects only. */
  onValueChange?: (value: string) => void;
  transform?: (raw: string) => string;
};

export type TextFieldProps = TextFieldBaseProps & FieldBindingProps;

export function TextField(props: TextFieldProps) {
  const [binding, rest] = splitBinding(props);
  const {
    label,
    description,
    optional,
    className,
    inputClassName,
    onBlur,
    onValueChange,
    transform,
    ...inputProps
  } = rest;
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={inputProps.disabled}
        >
          <Input
            {...inputProps}
            className={inputClassName}
            value={field.state.value ?? ""}
            onChange={(e) => {
              const next = transform
                ? transform(e.target.value)
                : e.target.value;
              field.handleChange(next);
              onValueChange?.(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
            onClear={() => field.handleChange("")}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
